import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { sendPasswordResetEmail } from 'firebase/auth';
import { db, auth, functions } from '../services/firebase';
import Topbar from '../components/Topbar';
import Icon from '../components/Icon';
import Loading from '../components/Loading';
import './MembersView.css';

export default function MembersView({ accountId, onBack, onLogout }) {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [busy, setBusy] = useState(null);
  const [ent, setEnt] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const currentUid = auth.currentUser?.uid;

  useEffect(() => {
    httpsCallable(functions, 'getEntitlements')({ accountId })
      .then(res => setEnt(res.data))
      .catch(() => setEnt(null));
  }, [accountId]);

  const maxAgents = ent?.limits?.maxAgents ?? -1;
  const atLimit = maxAgents !== -1 && members.length >= maxAgents;
  const inactive = ent && !ent.active;
  const blocked = atLimit || inactive;

  const fetchMembers = async () => {
    const q = query(collection(db, 'users'), where('accountId', '==', accountId));
    const snap = await getDocs(q);
    setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoadingList(false);
  };

  useEffect(() => {
    fetchMembers();
  }, [accountId]);

  const changeRole = async (member) => {
    const role = member.role === 'admin' ? 'agent' : 'admin';
    setError('');
    setBusy(member.id);
    try {
      const setMemberRole = httpsCallable(functions, 'setMemberRole');
      await setMemberRole({ accountId, userId: member.id, role });
      await fetchMembers();
    } catch (err) {
      console.error(err);
      setError(t('members.errors.changeRole'));
    } finally {
      setBusy(null);
    }
  };

  const removeMember = async (member) => {
    if (!window.confirm(t('members.removeConfirm', { name: member.name || member.email }))) return;
    setError('');
    setBusy(member.id);
    try {
      const removeMemberFn = httpsCallable(functions, 'removeMember');
      await removeMemberFn({ accountId, userId: member.id });
      await fetchMembers();
    } catch (err) {
      console.error(err);
      setError(t('members.errors.remove'));
    } finally {
      setBusy(null);
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const inviteAgent = httpsCallable(functions, 'inviteAgent');
      await inviteAgent({ accountId, email, name });
      await sendPasswordResetEmail(auth, email);
      setName('');
      setEmail('');
      await fetchMembers();
      setSuccess(t('members.inviteSent'));
    } catch (err) {
      console.error(err);
      setError(t('members.errors.invite'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <Topbar onBack={onBack} onLogout={onLogout} crumbs={[t('members.crumb')]} />
      <main className="page-main">
        <div className="mv-head">
          <h1>{t('members.title')}</h1>
          {ent && maxAgents !== -1 && (
            <p className="mv-count">{t('members.count', { current: members.length, max: maxAgents, plan: ent.limits.label })}</p>
          )}
        </div>

        {blocked && (
          <div className="mv-gate card">
            {inactive ? t('members.gateInactive') : t('members.gateLimit')}
          </div>
        )}

        <div className="card mv-form">
          <input className="field" placeholder={t('members.namePlaceholder')} value={name} onChange={e => setName(e.target.value)} />
          <input className="field" type="email" placeholder={t('members.email')} value={email} onChange={e => setEmail(e.target.value)} />
          <button className="btn btn-primary" onClick={() => handleInvite()} disabled={loading || blocked}>
            <Icon name="plus" size={17} />{loading ? t('members.inviting') : t('members.invite')}
          </button>
          {error && <p className="form-error">{error}</p>}
          {success && <p className="form-success">{success}</p>}
        </div>

        {loadingList && <div className="card"><Loading label={t('members.loading')} /></div>}
        <div className="mv-list">
          {!loadingList && members.map(m => (
            <div className="card mv-item" key={m.id}>
              <span className="mv-avatar">{(m.name || m.email || '?').slice(0, 2).toUpperCase()}</span>
              <div className="mv-who">
                <div className="mv-name">{m.name || t('common.dash')}</div>
                <div className="mv-email">{m.email}</div>
              </div>
              <span className="mv-role">{t(`members.roles.${m.role}`, { defaultValue: m.role })}</span>
              {m.role !== 'owner' && m.id !== currentUid && (
                <>
                  <button className="btn btn-ghost btn-sm" disabled={busy === m.id} onClick={() => changeRole(m)}>
                    <Icon name="rotate" size={15} />
                    {m.role === 'admin' ? t('members.makeAgent') : t('members.makeAdmin')}
                  </button>
                  <button className="btn btn-danger btn-sm" disabled={busy === m.id} onClick={() => removeMember(m)} aria-label={t('common.remove')}>
                    <Icon name="trash" size={15} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
